/**
 * tuning.ts — the single source of truth for how the game *feels*.
 *
 * Every gameplay number lives here with a name and a comment. Change a value
 * here and the whole game retunes; you never have to hunt through logic files.
 *
 * Units: distances are "world units" (~1 unit ≈ 1 metre for HUD readouts),
 * speeds are units/second, times are seconds, angles are radians.
 *
 * Starting values come from extensive playtesting of the original prototype.
 */
export const tuning = {
  /** Fixed-timestep simulation + frame pacing (see core/Loop.ts). */
  sim: {
    fixedHz: 120, // physics steps per second (decoupled from render frame rate)
    maxFrameMs: 250, // clamp huge frame gaps (e.g. returning from a backgrounded tab)
    // Applies offline AND online (M71): a bigger online cap was tried in M69 and
    // rendered post-hiccup catch-up as a raw teleport; the clock sync re-absorbs
    // dropped ticks invisibly, so the tight cap is right for both modes.
    maxStepsPerFrame: 8, // safety cap so we never "spiral" when a frame is very late
    // M111c: the APP caps draws at 60/s — ProMotion iPhones run WKWebView's
    // rAF at 120 Hz, doubling GPU heat for no visible gain (TestFlight:
    // "phones get hot"). Physics (fixedHz) is unaffected; web is uncapped.
    maxRenderHzNative: 60,
  },

  /** Online link-failure feel (M69) — what everyone SEES a lagging player's car
   *  do, so it's tuning, not plumbing. Ticks at 120 Hz (120 = 1 s).
   *  M73: the coast grace was 0.5 s and real home Wi-Fi stalls for 0.3–1.5 s
   *  every ~10 s (measured vs production — Apple Wi-Fi scans are the classic
   *  cause). Coasting DURING such a routine blip manufactures a divergence the
   *  player sees as a teleport when the pipe reopens; holding the last input
   *  through it predicts perfectly for steady driving. So: extrapolate ~1.5 s,
   *  then coast — the modern-netcode default. */
  net: {
    staleCoastAfterTicks: 180, // link silent this long (1.5 s) → the held input starts fading
    staleCoastRampTicks: 90, // …and reaches a full coast (zero throttle/steer) after this many more
    staleDropAfterTicks: 600, // 5 s of silence → the AI takes the wheel until input resumes
    // When the RENDER loop loses physics ticks to a main-thread stall, the
    // client re-simulates up to this many of them itself, at once and
    // fold-smoothed, so its clock never falls behind the server (M125; M143:
    // the loop now reports ALL the lost time, not just the first 250 ms).
    // 60 ticks = half a second. A longer freeze (a hidden tab) only moves the
    // clock label forward — the next snapshot re-derives the world.
    stallCatchUpMaxTicks: 60,
  },

  /** Renderer / global visuals. */
  render: {
    maxPixelRatio: 2, // cap device pixel ratio so dense phone screens stay fast
    shadowMapSize: 2048, // one directional shadow at a sane resolution
    cameraNear: 0.1,
    cameraFar: 1200,
    toneMappingExposure: 1.0,
    // M35 graphics overhaul. IBL reflection strength (scene.environmentIntensity):
    // how strongly the per-city sky reflects in paint/chrome/glass. 1 = physically
    // neutral; nudge down if a bright city blows out, up for more gloss.
    envMapIntensity: 1.0,
    // Bloom (pmndrs postprocessing) — makes emissive surfaces (neon, rails, head/
    // tail lights, boost flame, item boxes) actually glow. Gated off on Low quality.
    bloomIntensity: 0.7, // overall glow strength
    bloomThreshold: 0.7, // only the brightest pixels bloom (lights/neon/boost) — not lit walls or sky
    bloomRadius: 0.5, // how far the glow spreads
    // Loading round (best-in-class): DYNAMIC RESOLUTION while racing. The
    // render scale (× the quality preset's pixel-ratio cap) steps down when
    // the smoothed frame time stays over `slowMs` and steps back up only after
    // `recoverHoldMs` of frames under `fastMs` — a phone that can't hold the
    // budget at DPR 2 settles where it can instead of stuttering. `min` 0.6
    // of DPR 2 is DPR 1.2: still crisp on a 6" screen.
    dynRes: {
      enabled: true,
      slowMs: 24, // smoothed frame time above this → step down (~42 fps)
      fastMs: 17.5, // …and sustained below this → allowed to step back up (a clean 60 Hz passes)
      step: 0.12,
      min: 0.6,
      minIntervalMs: 1500, // never change twice within this
      recoverHoldMs: 6000, // how long frames must stay fast before stepping up
    },
  },

  /** Arcade car physics — the heart of the driving feel. */
  car: {
    // M49: marks the params object as the HUMAN engine (gets the M40 wall
    // bump/forward-escape). Was `params === tuning.car` — per-vehicle human
    // params (vehicleCarParamsFor) broke reference identity, so it's a flag.
    wallAssist: true,
    accel: 34, // forward acceleration off the line
    // M31 (owner): reverse now MIRRORS forward so course corrections snap — back
    // up as hard and as fast as you drive. NOTE this is the HUMAN car only; AI
    // rivals keep the original slow, controlled reverse (their k-turn / swing /
    // jam-bail maneuvers fire a fixed reverse throttle and rely on a LOW reverse
    // top as a natural governor) — pinned via ai.reverse* in aiCarParamsFor().
    reverseAccel: 52, // M39: snappier into reverse for fast course-correction (was 34)
    brake: 100, // M39: HARD brake so pulling the stick back stops fast, then reverses (was 58; AI pinned to 58)
    // M92 (owner: "sometimes reverse just acts as a brake and the car stalls"):
    // with the brake HELD, the car COMMITS to reverse once forward speed drops
    // below this crawl instead of demanding exactly 0. The old exact-zero gate
    // could be held open forever by any persistent forward nudge (ball resting
    // on the nose, a rival's shove, the M40 wall-escape kicks) — brake floors at
    // 0, the nudge re-adds a whisker, repeat = a permanent stall. HUMAN car only
    // (AI pinned to 0 in aiCarParamsFor — its k-turn/swing maneuvers are tuned
    // around the old timing). ONLINE-SYNCED — bump SIM_VERSION when touching.
    reverseEngage: 1.2,
    topSpeed: 56, // normal max forward speed
    reverseTop: 56, // max reverse speed — matched to forward `topSpeed` (was 15)
    // Wall-escape assist (M14): reversing while pinned on a guardrail used to
    // deadlock (steering could swing the tail INTO the wall faster than reverse
    // built speed, and the wall deleted it every step). While wall-pinned and
    // backing up, the car now eases its nose back toward the road, tempers the
    // player's steering, and gets a gentle off-the-wall nudge. Pure feel — no
    // change to normal driving.
    wallReverseYaw: 2.4, // how fast the nose eases toward the track direction (rad/s)
    wallReverseSteerScale: 0.45, // steering authority while pinned-reversing (can't re-jam)
    wallReverseKick: 7, // gentle off-the-wall acceleration while backing out
    // Forward wall-escape + bump (M40, HUMAN car only — the AI keeps the M14/M17
    // behaviour its recovery maneuvers are tuned around). Lets you steer OUT of a
    // rail going FORWARDS instead of reversing, and bumps cleanly off the wall.
    wallMargin: 0.15, // human clamps this far inside the rail so it never appears "through" it
    wallBumpRestitution: 0.2, // human wall bounce — a quick bump off (vs the 0.08 scrape below)
    wallForwardKick: 6, // outward "peel" once the nose is aimed off the rail, so you drive out fwd
    boostTop: 84, // max forward speed while boosting
    boostAccel: 44, // acceleration while boosting
    dragCoast: 0.6, // velocity bleed when coasting (no throttle)
    dragPower: 0.12, // velocity bleed under power (light)
    lateralGrip: 11.0, // sideways traction normally (planted, ~100% grip)
    driftGrip: 3.5, // sideways traction while boosting / trail-braking (slides)
    steerSmooth: 9.0, // how fast steering eases toward stick input
    steerReturn: 14.0, // how fast steering returns to centre (faster than it engages)
    baseTurnRadius: 5.0, // tightest turn radius at low speed
    turnRadiusGainPerSpeed: 0.3, // radius grows with speed (less twitchy at speed)
    lowSpeedTurnFloor: 2.5, // keeps low-speed turning usable
    maxYaw: 2.8, // HARD cap on turn rate (rad/s) — can never spin out from steering
    wallRestitution: 0.08, // walls scrape, they don't ping you back
    bodyRoll: 0.05, // visual only: bank into turns
    bodyPitch: 0.012, // visual only: pitch under accel/brake
    leanSmooth: 6, // visual only: how fast the body lean settles
    verticalFollow: 14, // visual only: how fast the body follows ground height (soft suspension)
    gravity: 30, // downward accel while airborne (off a launch ramp) — matches the ball
  },

  /**
   * Touch-control feel (mobile only). Desktop keyboard/gamepad NEVER read this —
   * the shaping is applied inside VirtualJoystick / Tilt, which only touch reaches.
   */
  touch: {
    // — Steering response curve (applied to the joystick axes; see input/touchTuning.ts).
    steerDeadzone: 0.06, // 0..1 of stick travel ignored at centre — kills nose jitter on touch
    steerExpo: 0.45, // 0 = linear, 1 = full cubic; eases SMALL inputs for fine ball control
    steerSensitivity: 1.3, // M39: base steer gain — keeps steering sharp despite the bigger throw (× user slider)
    // M115: 1/s low-pass on the REPORTED steer. 0 = OFF, and off is the adult
    // default BY CONSTRUCTION (smoothTowards returns the target unchanged), so
    // the grown-up stick stays the raw shaped axis it has always been.
    steerSmoothRate: 0,
    throttleDeadzone: 0.05, // small deadzone on the gas axis so a resting thumb doesn't creep
    throttleSensitivity: 1, // forward-gas gain — 1 = the M39 rule: raw 1:1 fine speed control
    // M92: extra gain on the DOWN half of the gas axis only — any pull into the
    // lower half of the stick reads as clear brake/reverse intent without the
    // thumb having to reach the bottom rim (endpoints preserved; forward gas
    // keeps its 1:1 fine control for speed-matching the ball).
    reverseSensitivity: 1.35,
    // — Joystick size / placement (was hardcoded in VirtualJoystick + ui.css).
    // NOTE baseRadiusPx is the ring's DIAMETER in px (it is the --joy-base CSS
    // var); the historical name stuck. maxRadiusPx IS a radius (the throw).
    baseRadiusPx: 160, // M39: bigger ring to match the longer throw (was 132)
    maxRadiusPx: 68, // M39: longer throw = more distance between slow and full (finer throttle; was 54)
    restRaisePx: 140, // how far above the bottom edge the resting pad sits (thumb zone)
    restInsetPx: 96, // how far in from the pad's screen side the resting centre sits
    // — Tilt steering: PARKED (M115, owner). Tilt was removed as a player
    //   option because it never steered well enough; the code (input/Tilt.ts)
    //   and these knobs are kept intact so the idea can come back in a future
    //   round. Nothing enables the gyro today, so these are inert.
    tiltSensitivity: 1.4, // curve gain like steerSensitivity (>1 = sharper near centre)
    tiltDeadzone: 0.05, // fraction of the tilt range ignored around the calibrated centre
    tiltMaxTilt: 0.35, // M39: less physical tilt for full lock (was 0.6 — owner had to tilt too far)

    /**
     * M115 — the BIG KID touch profile (kids difficulty only; see the M83 mode).
     *
     * WHY IT EXISTS (owner, watching his 4-year-old niece on an iPad): a small
     * child drives with a whole INDEX FINGER and big arm movements, not an
     * adult's short precise thumb arcs. Against the thumb-tuned pad above, a
     * small finger movement became a big steering command, every correction
     * overshot, and she slalomed down the course. This block replaces the
     * numbers — never the code path — while Big Kid is the active difficulty;
     * every other difficulty reads the adult values verbatim.
     *
     * The real fix is LOOP GAIN, not lag: a much longer throw + a much lower
     * sensitivity + a softer curve means the same finger travel produces far
     * less steer, so an over-correction can't run away. steerSmoothRate is a
     * light anti-tremor filter on top (see touchTuning.smoothTowards) — it is
     * deliberately fast, because ADDING lag to a human correction loop makes
     * oscillation worse, not better.
     *
     * All of the size numbers are TARGETS for a roomy screen; they are scaled
     * down to fit small phones by fitTouchProfile() (see the frac caps below),
     * so the pad can never run off-screen or swallow a landscape phone.
     */
    bigkid: {
      steerDeadzone: 0.16, // ~16% of the throw ignored — a wobbling finger at rest can't twitch the car
      steerExpo: 0.55, // softer than the adult 0.45: the middle of the pad barely turns
      steerSensitivity: 0.7, // adult is 1.3 — the headline "stop the slalom" knob (× the user slider)
      steerSmoothRate: 14, // 1/s anti-tremor low-pass (≈70 ms); raise for snappier, 0 to switch off
      throttleDeadzone: 0.08, // a drifting finger shouldn't slip into reverse
      throttleSensitivity: 1.5, // gas comes on with LESS reach, so the bigger pad never makes "hold forward" harder
      reverseSensitivity: 1.6, // a modest pull-back is a clear brake (kids need to stop, not feather it)
      baseRadiusPx: 320, // ring DIAMETER — 2× the adult pad; a genuine index-finger target
      maxRadiusPx: 140, // 2.1× the adult throw: full lock needs ~3 cm of finger, not ~1.4 cm
      restRaisePx: 200, // resting centre above the bottom edge
      restInsetPx: 180, // resting centre in from its screen side
      // Viewport fit caps — the resting pad may never exceed this fraction of
      // the live viewport. Height is the binding one on a landscape phone.
      padWidthFrac: 0.42,
      padHeightFrac: 0.62,
      minFitScale: 0.45, // never shrink below this much of the targets above
    },
  },

  /** The colour-matched soccer ball. */
  ball: {
    // M31 (owner): a bigger ball gives more surface area to dribble and strike,
    // so it's far harder to overshoot it at top speed — more arcade fun while
    // KEEPING the skill (steering/turn radius are untouched). Was 1.15; a touch
    // wider than the car now. The car-front "catch" widens for free because the
    // contact distance is CAR_BOX_HALF_LENGTH + radius (see collisions.ts).
    radius: 1.6, // M39 (owner): bigger again — easier to strike + keep at full speed (was 1.45)
    rollingResistance: 0.7, // M39: "lighter" — a struck ball keeps rolling further (was 0.7)
    restitution: 0.4, // bounciness off the ground
    wallRestitution: 0.6, // bounciness off the guardrails (rebounds into play, doesn't hug)
    gravity: 30, // downward acceleration
    carPush: 0.75, // base hard-strike kick (the AI uses this as-is, keeping its pace ladder)
    // M39: HUMAN-only multiplier on carPush (applied in SimCore for non-bot cars). Your full-speed
    // hits launch the ball further ahead so it stays in front (less overshoot/reversing) — the AI's
    // ball pace is untouched, so the difficulty ladder is preserved. Tune this for "how far it flies".
    humanPushScale: 1.5,
    // M12: raised above boostTop (was 46 < topSpeed 56 — the ball literally could
    // not keep up with a fast car, which is why it kept squirting off the nose).
    maxSpeed: 88, // ball can now match even a boosting car; rollingResistance reins in loose balls
    // Dribble cradle (M12) — what keeps a pushed ball CENTRED on the nose at speed.
    // Tuned so GENTLE steering corrections keep the dribble but a hard cut still
    // sheds the ball (it stays a skill, just not a fight).
    dribbleAlign: 0.5, // 0..1: bend front-face pushes toward the car's heading (0 = old raw normal)
    dribbleLateralGrip: 16, // per-second damping of the ball's sideways slip while on the nose
    dribbleCenter: 22, // centring pull toward the nose's middle (accel per unit of offset)
  },

  /**
   * BIG KID ball assist (M83, owner: "the user's possession of the ball is
   * greatly assisted — almost a magnet style assistance of ball control").
   * A soft always-on leash on the HUMAN's ball at the 'bigkid' difficulty
   * ONLY (kidAssistPull in collisions.ts, applied in SimCore.applyKidAssist):
   * within `range` of the car the ball's velocity is steered toward "ride
   * with the car + close the offset to the nose", capped at `pull` accel so
   * it reads as the ball WANTING to come back, never teleporting. A big punt
   * escapes the leash past `range` and the ball comes home when the kid
   * drives near it again. Offline-only by construction (online sims always
   * run 'legendary' — M76).
   */
  kidAssist: {
    range: 26, // leash length (units) — beyond this the ball is free until re-approached
    holdGap: 0.5, // resting spot this far off the nose (same units as magnetBallGap)
    gain: 2.2, // per-second pull closing the offset (higher = snappier return)
    pull: 55, // max velocity change per second (the "magnet strength" cap)
  },

  /**
   * POSSESSION TOUCH (M84, owner: "everyone — human and AI — should have a bit
   * more of a handle on the ball while dribbling, without Big Kid's magnet").
   * A soft always-on assist for EVERY racer's OWN ball (possessionPull in
   * collisions.ts, applied in SimCore.applyPossessionAssist): while the ball is
   * genuinely being DRIBBLED — in front of the car, inside `range` of the nose
   * spot, car driving forward, ball velocity riding close to the car's — its
   * velocity is gently steered to stay just off the nose, capped at `pull`
   * (≈1/4 of Big Kid's strength) so it reads as close control, never a magnet.
   * Possession is LOST exactly the ways the owner spec'd, with zero special
   * cases, because of two gates:
   *  - breakSpeed: ball velocity diverging from the car's beyond this = a real
   *    knock (rival car/ball, item, wall) OR a hard cut/brake at speed — the
   *    divergence in a turn grows with speed × turn angle, so slow tight turns
   *    keep the dribble while a top-speed cut sheds it, for free.
   *  - recedeSpeed: a ball actively ESCAPING the nose faster than this is a
   *    deliberate strike — let it fly (pulling punts back would kill the
   *    punt-and-chase game for humans and AI alike).
   * Per-vehicle strength comes from the HANDLING stat (possessionParamsFor —
   * bike grips the ball hardest, van weakest); weight stays in the collision
   * layer, so a heavy car's bigger knock breaks possession harder by itself.
   */
  possession: {
    range: 7.5, // dribble envelope (units from the nose hold spot) — ≈2 car lengths
    holdGap: 0.6, // resting spot this far off the nose
    gain: 1.4, // per-second pull closing the offset toward the nose spot
    pull: 14, // max velocity change per second (the "touch strength" cap)
    breakSpeed: 16, // ball-vs-car velocity divergence that counts as possession LOST
    recedeSpeed: 6, // ball escaping the nose faster than this = a strike, not a dribble
    minCarSpeed: 4, // the car must genuinely drive forward (a parked car never vacuums the ball)
    handlingInfluence: 2.5, // how hard the vehicle HANDLING stat swings the touch (see possessionParamsFor)
    minScale: 0.5, // floor on the per-vehicle scale (nobody drops below half touch)
  },

  /**
   * Power-ups (M12 — Mario-Kart style). Items are EARNED: drive through a
   * floating item box, a roulette rolls a random power (weighted by your race
   * position — trailing racers get the spicier stuff), and ONE item sits in
   * your slot until you fire it with the single use-item button. You must use
   * it before you can collect another. Same rules for the AI rivals.
   */
  powerups: {
    rouletteTime: 0.9, // seconds the HUD roulette spins before the item lands
    respawnTime: 7, // seconds before a collected box reappears
    collectRadius: 2.6, // how close a car must drive to a box to collect it
    ballRange: 10, // magnet/kick only work within this of YOUR ball (≈3 car lengths)
    staleItemTime: 10, // an AI dumps an unused item after holding it this long
    // M16: a rival dropped this far behind the PLAYER rolls from the TAIL tier
    // regardless of its place — the comeback kit (boosts, tackle) is how a
    // breakaway gets hunted once raw pace saturates (see ai.levels note).
    desperationGap: 250,
    // Effect durations (seconds).
    boostDuration: 2.4,
    magnetDuration: 4.0,
    dozerDuration: 4.0,
    shieldDuration: 6.0,
    kickDuration: 0.3, // just the strike flash — the kick itself is instant
    tackleDuration: 0.5, // the launch flash — the ghost does the rest (see tackle below)
    wallDuration: 0.4, // the plant flash — the dropped wall itself lives on (see wall below)
    offsideDuration: 0.4, // the flag flash — the stun lands instantly on everyone ahead
    pibeDuration: 7.0, // PIBE DE ORO: how long the golden run lasts (M58 owner: 5s was too short)
    studsDuration: 5.0, // STUDS OUT (M59): how long the spinning wheel spikes stay out
    scorpionDuration: 0.5, // SCORPION KICK (M59): the fire flash — the real state is scorpion.airTime
    mudDuration: 0.3, // SLIPPERY MUD (M59): the pour flash — the puddle lives on (mud.life)
    // Effect strengths.
    kickSpeed: 72, // how hard KICK fires your ball down-course
    kickPop: 2.5, // a little hop on the kicked ball so it skips over rough ground
    dozerLaunchForce: 46, // how hard the bulldozer flings a rammed rival
    dozerStun: 1.6, // seconds a rammed rival loses control (M115: was 1.1 —
    // the extra time is the GROUNDED daze after the tumble lands, so the stars
    // are actually readable instead of vanishing on touchdown)
    dozerTumble: 0.95, // M115: seconds of airtime for the victim's TWO flips
    dozerSpinSteer: 1.0, // how hard a rammed rival spins out while stunned (0..1 steer lock)
    magnetBallGap: 0.4, // gap between the car nose and the magnet-held ball
    shieldBumpFactor: 0.15, // fraction of a car-car shove a SHIELDED car still feels
    // SLIDE TACKLE (M14, owner-spec'd "blue shell"): a ghost of your car races
    // up the course, homes onto the LEADER, and slide-tackles them into the
    // nearest guardrail (a short stun). Only the bottom 33% of the field can
    // roll it; only a raised SHIELD blocks it.
    tackle: {
      ghostSpeed: 115, // how fast the ghost flies up the course (min — scales with the gap)
      homing: 6, // how fast it slides onto the target's lane (per second)
      shove: 38, // sideways launch speed into the guardrail
      keepForward: 0.35, // fraction of the victim's forward speed kept through the hit
      stun: 1.5, // seconds of lost control (owner: 1–2s)
      slideTime: 0.55, // how long the ghost's slide animation plays after contact
      maxChase: 7, // safety: the ghost gives up after this many seconds
    },
    // FREE KICK WALL (M46): plant a glowing three-man defensive wall across your
    // lane, just BEHIND your car. Chasing cars bump off it; balls rebound (your
    // own ball can't be smacked back past it — that's the point). It despawns
    // after `life`; you drive on, it stays where you dropped it. A Pibe de Oro
    // runner smashes straight through it.
    wall: {
      life: 8, // seconds the planted wall stands
      behind: 5, // planted this far behind the firer's car
      halfSpan: 5.5, // lateral half-extent (~a third of the road — leaves room to dodge)
      // M51 (owner): the wall is a DIVERTER, not a trampoline — a ball that
      // hits it is deflected SIDEWAYS toward the nearby guardrail at ~a right
      // angle (keeping most of its speed) instead of being fired back
      // down-course, so getting walled interrupts you without costing half a
      // minute of race time. Cars just take a soft bump and steer off.
      carRestitution: 0.12, // soft car bump (was 0.35 — too much bounce-back)
      ballDeflectKeep: 0.8, // fraction of ball speed kept through the sideways deflection
    },
    // OFFSIDE TRAP (M46, the "lightning bolt"): the flag goes up — EVERY racer
    // ahead of the firer spins out briefly. A raised SHIELD blocks it; a Pibe de
    // Oro runner is immune. The great equalizer for the back of the pack.
    offside: {
      stun: 1.0, // seconds of spin-out for everyone caught ahead
    },
    // PIBE DE ORO (M46, owner-spec'd "golden boy" star power): the RAREST item,
    // rolled ONLY by the last two places. For `pibeDuration` seconds the runner
    // gets the full golden kit: the ball rides the nose like a magnet (latches
    // when you get within `ballRange`), the engine runs at boost speed, and
    // anything in the way — cars, rival balls, free-kick walls — is smashed
    // aside with a spin-out. NOT blocked by shields (the star tops everything).
    pibe: {
      // M51 (owner): a real comeback chance, not a unicorn — ~23% of rolls
      // for the SECOND-TO-LAST place (weight vs the ~100-point tail table
      // below)…
      weight: 30,
      // …M58b (owner): DEAD LAST's golden hand reaches further — ~30%.
      weightLast: 42,
      // …and both climb when the eligible racer is ALSO a significant
      // distance off the back of the pack (the desperationGap test): ~35%
      // second-to-last, ~41% dead last. The further gone you are, the
      // likelier El Diez answers.
      weightDesperate: 55,
      weightDesperateLast: 70,
      launchForce: 42, // how hard a smashed car is flung aside (cf dozerLaunchForce 46)
      stun: 1.1, // seconds a smashed car spins out (SHIELDED cars: pushed, never spun — M51)
      ballBlast: 3.0, // carPush multiplier vs RIVAL balls — they're blasted out of the path
    },
    // STUDS OUT (M59, owner): big spinning silver spikes burst out of the
    // wheels while active — any rival CAR that touches them spins out, and any
    // rival BALL that touches them is fired sideways into the guardrail. A
    // front-of-pack item (rolled by the leader + the lead tier): defend your
    // lead by shredding anyone who closes in. SHIELD blocks the spin (M51
    // doctrine); a PIBE runner is untouchable; an already-stunned car can't be
    // re-locked. ONLINE-SYNCED — bump SIM_VERSION when touching these.
    studs: {
      stun: 0.9, // spin-out seconds for a rival caught on the spikes
      launchForce: 30, // shove speed applied to a caught rival
      ballReach: 3.9, // car-centre→ball distance that counts as a spike strike
      ballDeflect: 52, // speed the struck rival ball is fired toward the guardrail
    },
    // SCORPION KICK (M59, owner): the car kicks the ball high into the air and
    // backflips; the ball sails a long way down-course, UNTOUCHABLE in flight
    // (no cars/balls/obstacles interfere — only gravity + the guardrails), and
    // bounces normally once it lands. The firer is locked in place doing the
    // flip for `airTime`, then regains control to chase. A last-two-places
    // comeback item, NOT rollable in the final `maxCourseFrac` of the course.
    // M60b (owner): a TWO-PHASE bicycle kick. (1) the car pops the ball
    // STRAIGHT UP directly above itself; (2) when the ball reaches its apex the
    // car LEAPS and FLIPS — the scorpion kick — volleying the ball high + far
    // down-course. The ball flies untouchable the whole time and lands ~300 u
    // ahead. Last-two only, banned in the final stretch (maxCourseFrac).
    scorpion: {
      weight: 30, // roll weight vs the ~100-pt tail table (last-two only)
      // Phase 1 — the pop-up: ball launched straight up (no forward), apex at
      // kickUpTime (kickUp = gravity·kickUpTime = 30·0.65 ≈ 19.5, peak ≈ 6 u).
      kickUp: 19.5,
      kickUpTime: 0.65,
      // Phase 2 — the volley at the apex: a big up + down-course kick. The
      // ball's horizontal speed is capped at ball.maxSpeed (88) with no air
      // drag → forwardSpeed set to the cap; upSpeed 48 lifts the peak to ≈ 45 u.
      // Volley flight ≈ 3.3 s → ~290 u forward. The CAR leaps + flips over
      // flipTime, then lands and drives while the ball is still sailing.
      upSpeed: 48,
      forwardSpeed: 88,
      flipTime: 1.4,
      // Total ball immunity = kickUpTime + the volley flight (≈ 4.0 s).
      airTime: 4.0,
      maxCourseFrac: 0.7, // NOT rollable once the firer is past this fraction of the course
      // M60d (owner): a DEAD-LAST racer this far (along-course) behind the
      // NEAREST rival is AUTO-GRANTED a scorpion as their next item — a
      // guaranteed lifeline (still capped at SCORPION_MAX per match + the
      // course gate). ≈ the arc-length a scorpion advances the ball
      // (forwardSpeed × flight ≈ 295 straight-line, ~230 along a curving road).
      autoGrantGap: 230,
    },
    // SLIPPERY MUD (M59, owner): pour a puddle of mud out the back — every
    // RIVAL car (and their ball) that crosses it slows down and loses traction
    // while on it. The puddle lasts as long as a free-kick wall (`wall.life`).
    // A top-two item: deny the chasers their line. One puddle per owner
    // (replanting moves it). ONLINE-SYNCED — bump SIM_VERSION when touching.
    mud: {
      weight: 22, // roll weight injected for 2nd place (1st has its own in weights.first)
      radius: 7.5, // puddle radius on the ground (M60: bigger — easier to see + harder to dodge)
      behind: 5, // dropped this far behind the firer's car
      carDrag: 1.35, // per-second velocity bleed for a rival car on the puddle
      ballDrag: 1.7, // …and for a rival ball rolling through it
    },
    // Item-roll odds by race position (relative weights, not percentages).
    // M14 (owner #9) + M46 rework: the LEAD tier rolls NO boosts (they don't
    // need speed) and leans defense (shield + the free-kick WALL); the TAIL
    // tier (bottom 33%) rolls the comeback kit — boosts, dozer, the SLIDE
    // TACKLE and the OFFSIDE TRAP. PIBE DE ORO is handled separately in
    // rollItem: only the LAST TWO places can roll it (weight above).
    // M58 (owner): FIRST PLACE is defense-ONLY — shield or free-kick wall,
    // nothing else, ever. Items are the comeback channel; the race leader
    // never rolls an attack or speed item (magnet/boost/kick/dozer/offside/
    // tackle/pibe). The `lead` tier below still covers the REST of the top
    // third (2nd/3rd in a big field keep magnet + kick).
    // M59: three new items slot in by position. STUDS OUT is a front-of-pack
    // defence — it rides the `first` + `lead` tables. SCORPION KICK (last-two,
    // course-gated) is injected in rollItem. SLIPPERY MUD is injected for 2nd
    // place, but FIRST PLACE carries its own mud weight in the `first` table
    // (M60 owner: the leader should get mud + wall MORE than shield + studs).
    weights: {
      // M60 (owner): leader defence, mud + wall favoured over shield + studs.
      first: {
        boost: 0,
        magnet: 0,
        dozer: 0,
        shield: 18,
        kick: 0,
        tackle: 0,
        wall: 30,
        offside: 0,
        pibe: 0,
        studs: 18,
        scorpion: 0,
        mud: 34,
      },
      lead: {
        boost: 0,
        magnet: 22,
        dozer: 4,
        shield: 30,
        kick: 24,
        tackle: 0,
        wall: 20,
        offside: 0,
        pibe: 0,
        studs: 16,
        scorpion: 0,
        mud: 0,
      },
      mid: {
        boost: 22,
        magnet: 18,
        dozer: 14,
        shield: 8,
        kick: 22,
        tackle: 0,
        wall: 8,
        offside: 8,
        pibe: 0,
        studs: 0,
        scorpion: 0,
        mud: 0,
      },
      tail: {
        boost: 26,
        magnet: 8,
        dozer: 12,
        shield: 0,
        kick: 14,
        tackle: 22,
        wall: 0,
        offside: 18,
        pibe: 0,
        studs: 0,
        scorpion: 0,
        mud: 0,
      },
    },
    // Where the floating item boxes sit on the course (deterministic per seed).
    boxes: {
      firstRow: 140, // first row this far past the start line
      spacing: 175, // distance between rows
      spacingJitter: 25, // ± random wobble on each gap
      laterals: [-10, 0, 10], // a row = 3 boxes spread across the road
      lateralJitter: 2, // ± per-box sideways wobble
      hover: 1.7, // box centre height above the road
      finishClear: 150, // no boxes this close to the finish
      rampClear: 25, // no boxes this close to a launch ramp
    },
  },

  /**
   * Per-vehicle stat profiles (M49; REAL in every mode since M51; M104 UPGRADE
   * LADDER — owner: "each unlocked vehicle must feel like an increase in
   * overall ability", retiring the M49 trade-off doctrine). Each multiplier is
   * relative to the stock `car` block above:
   *  - topSpeed × top speed + boost top       - accel × accel + boost accel
   *  - handling: tighter turn radius + higher yaw cap (>1 = nimbler) AND the
   *    M84 possession-touch strength (possessionParamsFor)
   *  - weight: car-car shoves (heavier = shoved less, shoves more) + a touch
   *    of ball-strike heft. THE M57 FIVE ARE DELIBERATELY UNCHANGED — those
   *    clash tiers (truck spins bike etc.) are tuned to a knife's edge, and
   *    weight stays the FLAVOR axis of the mid-ladder. M114's f1 tops the
   *    scale at 1.6 (the new heaviest — in this game's clash physics heavier
   *    is strictly advantageous, so "maxed" includes weight).
   * THE LADDER RULE (M104, test-enforced in tests/sim/vehicles.test.ts): in
   * unlock order (coupe → pickup → muscle → sports → van → motorcycle → f1)
   * the DRAG-EQUILIBRIUM sustained speed — accel/(accel/topSpeed + dragPower),
   * ≈ 46.8 u/s for the pre-M104 stock car — strictly ASCENDS, and no unlock's
   * handling (ball touch) ever drops below the starter coupe's. Sustained
   * speeds: coupe 44.3 → pickup 47.9 → muscle 51.3 → sports 53.3 → van 53.4
   * → motorcycle 55.6 → f1 56.6 u/s.
   * WHO uses what (M51): every HUMAN — offline and online — drives its
   * vehicle's full profile (vehicleCarParamsFor). BOTS keep the tuned
   * aiCarParamsFor ENGINE whatever vehicle they appear in — but their vehicle
   * WEIGHT is real in car-car shoves, and their POSSESSION TOUCH follows
   * their vehicle's handling too. Online, each racer's vehicle syncs via
   * snapshots — so CHANGING ANY NUMBER HERE changes online sim behaviour:
   * bump SIM_VERSION with it.
   */
  vehicles: {
    // M153 `box` (owner: "the ball shouldn't meet one shared box — wider
    // vehicles should have a wider box"): the BALL-CONTACT box each vehicle
    // presents (sphere-vs-box in collisions.ts — the dribble, the strike, the
    // nose spot the magnet / possession touch / kid leash hold the ball at).
    // halfLength = half the ride's on-course length (vehicles.ts `length` / 2,
    // test-pinned — the ball meets the real nose and tail, not a shared
    // 3.8-unit box every ride overhung or sat inside). halfWidth = the
    // measured body half-width at that scale (scratchpad measure_glb.py,
    // mirrors excluded) + a CATCH margin, so a ball doesn't slip off a nose
    // corner at speed (the M12 owner request): +0.25 on the cars (the old
    // shared 1.15 gave the sports car +0.23), +0.41 on the bike (owner: a
    // narrow ride may "grab the ball a bit further outside its true nose";
    // its 0.54 true half-width would make a dribble hopeless). Car-car
    // contact stays the shared CAR_RADIUS circle. ONLINE-SYNCED SIM
    // BEHAVIOUR — bump SIM_VERSION when touching these (M153 = sv27).
    // The STARTER coupe (M62/M84b) — untouched in M104: the owner tuned its
    // nimble sticky-ball first impression by hand. Everything unlocks UP from
    // here. Box: 3.8 long (the old shared length), body 1.64 wide.
    coupe: { topSpeed: 0.95, accel: 0.94, weight: 1.0, handling: 1.1, box: { halfLength: 1.9, halfWidth: 1.07 } },
    // Unlock 1 (@30 SP): the heavyweight that's faster EVERYWHERE than the
    // coupe — pace, launch, touch — plus the 1.35 weight to bully with.
    // Box: 4.4 long (M154: the bumpers tucked in to the concept pack's ends
    // at today's on-screen scale and width, owner 2026-10-21; was 4.65), tyres 1.95 wide
    // (M154 round 3: the wheels 2 cm in, today's overall width at the tyres).
    pickup: { topSpeed: 1.02, accel: 1.04, weight: 1.35, handling: 1.12, box: { halfLength: 2.2, halfWidth: 1.22 } },
    // Unlock 2 (@70 SP): real top-end arrives (boost top 92), still heavy.
    // Box: 4.5 long, body 1.77 wide.
    muscle: { topSpeed: 1.1, accel: 1.08, weight: 1.25, handling: 1.14, box: { halfLength: 2.25, halfWidth: 1.14 } },
    // Unlock 3 (@140 SP): the precision speed machine — quickest touch + turn
    // of the cars; trades the heavyweights' shove authority for it.
    // Box: 4.1 long, body 1.98 wide (M154: the owner's taller, wider body,
    // 2.19 m across the rear arch lips at the pack's proportions, the raw
    // body 4.54 long with the round-2 pointed nose; was 1.83).
    sports: { topSpeed: 1.14, accel: 1.14, weight: 1.0, handling: 1.18, box: { halfLength: 2.05, halfWidth: 1.25 } },
    // Unlock 4 (@200 SP): 'van' is the MONSTER TRUCK since M56 (id kept for
    // wire compat) — the hardest launch in the game (accel 1.2) at sports-class
    // pace, wearing the 1.5 weight that spins bikes and staggers sports cars.
    // Box: 4.6 long, 2.61 wide across the giant tyres — the widest nose in
    // the game, a wall of a bumper.
    van: { topSpeed: 1.13, accel: 1.2, weight: 1.5, handling: 1.16, box: { halfLength: 2.3, halfWidth: 1.56 } },
    // Unlock 5 (@600 SP): the apex two-wheeler — faster and stickier than any
    // car below the crown; its 0.7 weight (fragile in clashes) stays its one
    // honest risk. Box: 2.6 long (the ball meets the front tyre), 1.08 wide
    // across the levers + the owner's grab margin.
    motorcycle: { topSpeed: 1.18, accel: 1.24, weight: 0.7, handling: 1.25, box: { halfLength: 1.3, halfWidth: 0.95 } },
    // Unlock 6 (@1000 SP, M114): the ultimate machine — the owner's "all
    // full-bar maxxed out stats". Each stat sits EXACTLY at its garage
    // STAT_BARS window max (ui/vehicleCatalog: topSpeed 1.2, accel 1.26,
    // handling 1.28, weight 1.6) so every bar renders full without moving any
    // other ride's bar. Fastest sustained (56.6), strongest touch (1.7×), and
    // the new heaviest — nothing spins it, it staggers the sports car.
    // Box: 4.8 long (the front wing's leading edge is the nose), 1.76 wide
    // across the wheels — the wing spans the whole box front.
    f1: { topSpeed: 1.2, accel: 1.26, weight: 1.6, handling: 1.28, box: { halfLength: 2.4, halfWidth: 1.13 } },
  } as Record<
    string,
    { topSpeed: number; accel: number; weight: number; handling: number; box: { halfLength: number; halfWidth: number } }
  >,

  /**
   * M57 weight-clash impacts — the reason to pick a heavy or light ride. When
   * two cars collide at real speed, their tuning.vehicles WEIGHTS are compared:
   * at `spinRatio`+ disparity the LIGHT one SPINS OUT (a truck swats the bike);
   * at `staggerRatio`+ it staggers (a beat of lost steering — the monster truck
   * rattles even the sports car); below that it's just the plain 1/weight
   * shove. The pairs (weights 1.0/1.25/1.35/1.5/0.7, + M114 f1 1.6):
   *   bike hit by:   f1 2.29 SPIN · monster 2.14 SPIN · pickup 1.93 SPIN
   *                  · muscle 1.79 stagger · sports 1.43 bump
   *   sports hit by: f1 1.60 stagger · monster 1.50 stagger · pickup 1.35 bump
   *                  · muscle 1.25 bump
   *   coupe hit by:  f1 1.60 stagger (same 1.0 weight as sports)
   *   f1 hit by:     nothing above 1.07 — the crown neither spins nor staggers
   *                  (muscle 1.28 · pickup 1.19 · monster 1.07, all bumps)
   *   all other pairs (ratio < 1.5): plain bump.
   * SHIELD blocks the stun (still shoved — the M51 pibe rule); PIBE pairs never
   * reach here; finished racers neither spin nor cause spins. Applies to AI
   * and humans alike (M51: physics match the visuals in every mode).
   * ONLINE-SYNCED SIM BEHAVIOUR — bump SIM_VERSION when touching these.
   */
  clash: {
    spinRatio: 1.9, // full spin-out at/above this weight ratio
    staggerRatio: 1.5, // stagger at/above this ratio
    minImpactSpeed: 9, // closing speed (u/s) that counts as a hit, not a rub
    spinStun: 0.9, // seconds of spun-out (bulldozer-style steer lock)
    staggerStun: 0.35, // seconds of stagger wobble
    staggerSteerScale: 0.6, // stagger wobbles gentler than a full spin
    // On a clash the HEAVY one PLOWS THROUGH: its (already 1/weight) shove
    // share is scaled by this, so a bike T-boning the monster truck barely
    // nudges it — and the truck swats without losing its own momentum.
    heavyPlow: 0.35,
  },

  /**
   * Interactive course features (M47) — boost pads / slick patches / obstacles,
   * hand-placed per city in config/cities.ts. These are the FEEL knobs.
   * ONLINE-SYNCED SIM BEHAVIOUR — bump SIM_VERSION when touching these.
   */
  features: {
    // M99 (owner: "pads don't show any big difference"): pads now HIT — an
    // instant speed kick the moment you touch one, plus a longer surge tail.
    boostTime: 1.6, // seconds of full boost-engine surge after leaving a pad
    padKick: 12, // instant forward u/s added on pad ENTRY (never past boostTop)
    slickGripScale: 0.32, // lateralGrip multiplier while on ice/puddle/sand (1 = no slide)
    // M51 (owner): obstacles are DIVERTERS — a car or ball that hits one
    // slides AROUND it tangentially, keeping most of its speed, instead of
    // bouncing backward. They interrupt your line, never your race.
    obstacleBallKeep: 0.85, // fraction of ball speed kept sliding around an obstacle
    obstacleCarKeep: 0.75, // fraction of car speed kept sliding around an obstacle
    // M99 moving obstacles (crossing city traffic): same diverter doctrine,
    // slightly gentler keeps — getting clipped by the bus should sting, not end
    // the race. Per-city timetables live in cities.ts `movers`.
    moverBallKeep: 0.85, // fraction of ball speed kept glancing off a mover
    moverCarKeep: 0.7, // fraction of car speed kept glancing off a mover
    /**
     * THE FORD (nyc-lake, owner: "have the water flowing under the bridge on
     * the course as well with a make-shift plank bridge crossing the drivers
     * and their balls over the water... if the racers don't go over the plank
     * area then they will slow down crossing the water portion").
     *
     * A ford is a band of open water across the road with a dry PLANK window
     * through it. Wading costs SPEED — the drag pair below is the whole feel —
     * plus a mild loss of bite. It is deliberately NOT a wall: a full-speed
     * car crosses, it just pays for it, and the plank stays clearly the fast
     * line. Placement (band length, plank width) lives per city in
     * cities.ts `features`.
     */
    ford: {
      gripScale: 0.62, // lateralGrip multiplier while wading (mild — water is not ice; the drag is the point)
      carDrag: 2.0, // per-second velocity bleed for a wading CAR
      ballDrag: 2.0, // …and for a BALL rolling through the water
      /**
       * THE CURRENT — and it is a structural guarantee, not decoration. Under
       * drag alone a ball that stops in the water stays there for the rest of
       * the race, and standings rank by BALL progress, so a ford would be able
       * to end someone's race. The owner's own words are "water FLOWING under
       * the bridge": inside a ford the ball's DOWN-COURSE speed is held at (at
       * least) `current`, so any ball in the water always drifts out of it in
       * bounded time — see tests/physics/ford.test.ts.
       *
       * Cars get nothing: they have an engine, and a conveyor under a driven
       * car reads as a bug. 5 u/s is ~1/8 of race pace, so drifting out is
       * never a shortcut — it is the slowest possible way through.
       */
      current: 5, // down-course speed the water holds a ball at (u/s)
      currentAccel: 26, // how briskly the flow picks a stalled ball up (u/s²)
    },
  },

  /**
   * SECRET SHORTCUTS (M117c aerial rework — the owner's second round: "no
   * more tunnels or ground-level entrances; make all the shortcuts from
   * kicker ramps"). Every shortcut is a RAMP-LAUNCHED AERIAL PATH: hit a
   * launch ramp with real speed, FLY, and catch an elevated deck (a cavern
   * mouth up a cliff, a park high-line beside the towers, a sky-path over
   * the lagoon) — or thud back down, Koopa-Troopa-Beach style. You drive
   * the deck yourself; a descent chute drops you back through a wide exit
   * opening at road grade (exits are not entrances — the flight is the
   * gate). Humans only: decks are uncatchable for bots, so the locked pace
   * ladder is untouched. Specs in cities.ts `shortcuts`; pure physics in
   * physics/shortcuts.ts.
   */
  shortcuts: {
    // M117e (owner: "still a bit too easy to get the shortcut — make the ramps
    // slightly further away from the platform and the accuracy margin a bit
    // slimmer"). The two levers compound, MEASURED: a launch line that crosses
    // the wall at a shallow angle turns aperture width into lateral tolerance
    // as (mouthHalf − radius)·sin(angle), so pushing the kicker further back
    // both lengthens the flight (raising the speed you must carry) AND
    // narrows the angle (tightening the aim). Trimming the aperture on top
    // gives the final margin. Entry bars measured after: 22 → 34-36 u/s
    // staged, lateral tolerance ±1.0 → ±0.5 u.
    mouthHalf: 3.5, // wall-aperture half-width (cavern mouth / highline catch edge)
    catchHalf: 4.4, // skypath landing-porch half-width (the road curves under a straight flight so player lines still spread ±10°, but the old 6.0 forgave a ±6 u miss — measured)
    catchDepth: 11, // skypath porch depth: land on the deck's front stretch, not a knife-edge plane — but sail past it and you are out
    catchAhead: 44, // skypath: the porch hangs this far past the ramp lip on its flight line — a genuine LONG jump, and the flight's natural drift parks the porch at the course-wall edge (owner: entrances at the wall, far from the ramps)
    deckStandoff: 13, // highline: the deck's leading edge sits this far BEYOND the wall crossing — the landing tucks outside the course among the scenery
    exitHalf: 4.0, // exit opening half-width (wide — leaving is never the test)
    cavernHalf: 3.4, // drivable half-width inside the cavern gallery
    deckHalf: 3.1, // drivable half-width on the open decks (rails clamp like guardrails)
    interiorH: 6.4, // cavern interior height (chase cam rides ~4.8 over the car — measured M116)
    mouthLift: 6.4, // cavern mouth FLOOR above road grade (kept for the Rio redo)
    highlineLift: 6.3, // highline crossing SILL above grade at the wall — the longer flight must still be climbing here (threshold ≈ 39 launch speed; the ramp boost puts a committed ~30+ arrival there)
    skypathLift: 8.2, // skypath deck floor above the ramp-lip grade — the long 34 u flight sags, so the deck sits high and only committed launches reach it
    sillDrop: 0.3, // porch forgiveness below the deck floor (skypath/cavern: land ON it)
    // M117d (owner: "have the shortcuts be mostly downhill so that if a ball
    // makes it onto the shortcut without the user it continues down the path
    // to the exit"). Two halves: the deck now FALLS continuously from the
    // catch to the exit (no level glide + cliff chute), and inside a channel
    // the ball rolls like it is on a polished chute — light surface drag plus
    // an exaggerated slope pull, because a real 2-4% deck at this game's
    // gravity would trickle a loose ball at walking pace (measured). Both are
    // CHANNEL-LOCAL: road ball physics — and therefore the locked AI pace
    // ladder — is untouched by construction (and bots can never enter).
    deckClearance: 3.2, // the falling deck never sinks nearer than this to the ground it crosses (tapers to 0 at the exit, where it lands ON the road) — it is a SKY path, so it stays clear of banks and water
    // M117 batch 2 — CREST RELIEF (Tokyo forced it, measured): when the ground
    // under a deck RISES above the catch height (Tokyo's street crests 8.9 at
    // 19.5% — 0.16 u ABOVE the highest catchable deck start), the clearance
    // floor + the catch-height cap used to clip the profile into a dead-LEVEL
    // shelf (78 u on the expressway spur) — and a level deck has ZERO chute
    // pull, so a dead-stopped ball stranded forever, the exact case the
    // downhill rule exists to kill. Where (and ONLY where) the profile passes
    // leave such a shelf, it is tilted into this minimum strict fall, letting
    // the clearance compress toward shelfMinClearance over the crest — the
    // downhill rule outranks the sky-line floor. Batch-1 profiles have no
    // shelf, so they are byte-identical by construction (test-pinned).
    shelfGrade: 0.008, // min fall per unit across a terrain-forced shelf (pull ≈ chuteSlopeGravity × 0.008 ≈ 1 u/s² — a dead ball crosses a 78 u shelf in ~15 s)
    shelfMinClearance: 0.8, // the compressed floor over a crest (the deck may hug a rise this close; it still never digs in)
    chuteRolling: 0.22, // ball rolling resistance on a deck (the road's is 0.7 — boards and concrete are smooth)
    chuteSlopeGravity: 120, // effective gravity for the along-deck slope pull (real g = 30): a 2% deck rolls a stray ball out at ~8 u/s, a 4% one at ~16 — brisk enough that a stranded ball rejoins the race in seconds, gentle enough that it never outruns a driver
    launchBoostTime: 1.5, // seconds of engine surge granted by a shortcut launch ramp (the "speed boost on the ramp", humans only — bots never enter, so bots never need it and the pace ladder stays untouched)
    // M117e: the ramp's INSTANT kick, deliberately smaller than a boost pad's
    // (features.padKick 12). MEASURED: at the pad's full 12 the ramp simply
    // normalized every arrival — a 20 u/s crawl was lifted over the sill just
    // like a 46 u/s charge, so the car's speed stopped mattering at all
    // (arrival bars were 20). A small kick keeps the ramp feeling alive while
    // the SURGE (boost engine, which accelerates from whatever you brought)
    // does the rest — so a committed run is rewarded and a crawl is not, the
    // owner's "jump accuracy and speed of car are most relevant to success".
    launchKick: 5,
    launchMagnetTime: 1.8, // seconds the launch ramp's TEMPORARY MAGNET holds the ball on the nose — covers the flight + the landing settle, so accuracy and speed decide entries, not ball luck
    launchMagnetRange: 7, // the carry only engages with the ball already NEAR the car — it keeps a dribble together, it never yanks a lost ball across the road
    doorAngle: 0.46, // rad the spine angles off-course at the exit crossing (~26°)
    stubLen: 8, // channel stub reaching INSIDE the road at the exit (transition continuity)
    chStep: 3, // channel node spacing (the channel is a real mini-Track)
    minOutward: 1.5, // u/s toward the aperture to count as flying AT it
    refineSpan: 0.02, // placement may slide a kicker ± this course fraction to the straightest runway
    approachLen: 90, // straight RUNWAY a side-kicker demands (speed must be buildable)
    // Side kickers, aimed at their aperture. GEOMETRY IS THE THRESHOLD
    // (measured on the Rio flight): the lateral run to the wall must take as
    // long as the climb to the sill — a kicker hugging the wall arrived
    // 0.2 s before the height did, got wall-clamped, and the catch went
    // dead. Both compute to ~33-34 u/s entry thresholds, flat kinematics.
    kicker: { back: 20, inset: 8, length: 8, halfWidth: 2.6, height: 3.2 }, // cavern (Rio)
    kickerBig: { back: 34, inset: 11, length: 10, halfWidth: 3.0, height: 3.4 }, // highline (NYC/Tokyo/Napoli) — a ramp set inboard and set BACK: M117e pushed `back` 26 → 34, so the flight to the wall-edge deck is a real long jump (~37 u lip-to-landing) that only a committed run carries, and the shallower crossing angle tightens the aim margin with it. The deep inset also keeps NYC's aim walking the deck past Bow Bridge's ±33 u banks (measured)
  },

  /**
   * AI rival driver — how the computer plays "billiards" with its ball down the
   * course (see ai/Driver.ts): it lines up behind the ball and KNOCKS it toward a
   * point further down the racing line, lets it roll (banking off the guardrails
   * if it gets loose), catches back up, and knocks it again. The structural knobs
   * here are shared by every rival; the per-DIFFICULTY skill knobs live in
   * `levels` below. Owner tweaks both freely.
   */
  ai: {
    // ---- Shared structure (the same at every difficulty) ----
    // RACING LINE: the path we want the BALL on — the centre, cut a little to the
    // INSIDE of the upcoming bend. `cornerLookahead` scans ahead for the sharpest.
    insideLineBias: 5, // inside offset per radian of bend (units/radian)
    insideLineBiasMax: 8, // never cut more than this far inside (units)
    cornerLookahead: 40, // units ahead to scan for the sharpest upcoming bend

    // PUSH steering: pure-pursuit the BALL's lane — aim a look-ahead point on the
    // ball's lateral and steer at it, so the car curves in BEHIND the ball and knocks
    // it down-course (instead of arriving beside it and circling). The look-ahead
    // GROWS with speed (M12) so a fast chase stays smooth instead of fishtailing.
    pushLookAheadBase: 7, // aim point this far ahead of the CAR at a standstill…
    pushLookAheadPerSpeed: 0.16, // …plus this much per unit of speed (≈15 at full pace)
    lineCorrect: 0.0, // ease that lane gently toward the racing line (0..1; small = mostly straight)
    steerGain: 1.1,

    // SPEED: drive toward the level's cruise speed, eased for the upcoming bend and
    // when steering hard (so it can turn tightly instead of circling at speed).
    speedGain: 0.1, // throttle per unit of speed-under-cruise
    // M104: cruise-target multiplier while a boost effect is LIVE (item boost,
    // the pibe golden run, or a boost pad's surge). The P-controller chases
    // (cruise − vf), so without this a boosting bot idled at its plain cruise
    // number — boost bought it ~3 u/s where a human gains ~28 (measured; the
    // comeback items the tail tiers roll were near no-ops for bots). Corner
    // ease still applies to the boosted target, so bends stay survivable.
    boostCruiseScale: 1.5,
    // M12: corner braking is PROPORTIONAL to cruise (was a flat subtraction) so
    // fast levels shed enough speed for the same bend — every level corners at a
    // survivable pace and the difficulty gap lives on the straights.
    cornerEase: 1.0, // fraction of cruise shed per radian of upcoming bend
    hardSteer: 0.9, // steering error (rad) at which speed is cut to minSpeedFrac
    minSpeedFrac: 0.22, // never drop the target below this fraction of cruise

    // APPROACH control (M12) — the big lap-time fix at high cruise speeds: when
    // closing on the ball, cap speed to "ball speed + a gap-proportional closing
    // allowance" so the car arrives at a matched speed, touches, and accelerates
    // WITH the ball — instead of blasting past it into a brake-and-reverse cycle
    // (pre-fix, legendary wasted ~50s/race in RECOVER thrash).
    approachWindow: 50, // only modulate speed within this gap to the ball (wide enough for buffed cars to brake)
    approachGain: 1.1, // closing speed allowed per unit of gap
    // M104: 5 → 8. In the dribble regime the approach cap (ballFwd +
    // touchSpeed) IS the bot's speed governor, and the P-controller only
    // commands (touchSpeed × speedGain) throttle there — 5 meant HALF
    // throttle while dribbling, the single biggest measured pace loss. 8
    // self-gates on the low tiers: easy/medium's cruise caps bind first, so
    // this mostly lifts hard/legendary's carried pace.
    touchSpeed: 8, // speed-over-ball always allowed (the continuous dribble push)
    touchGap: 4, // inside this gap we're "on" the ball (≈ nose contact)
    strikeMin: 24, // never tiptoe below this — a slow/stuck ball still gets a solid knock
    // M77 hairpin guard, LIVELY-BALL levels only (ballPush > 1): Legend's
    // harder strikes ping-ponged off hairpin walls (Patagonia solo 134→184 s,
    // measured) — inside a sharp bend the strike floor eases off so the ball
    // stays playable instead of banking hard up-course.
    hairpinBend: 0.62, // |bend| (rad) past which the guard engages
    hairpinStrikeScale: 0.65, // strikeMin multiplier inside such a bend

    // PUNT mode (M14, owner: "hard/legendary too easy, too focused on a paced
    // dribble"). On straights, punt-capable levels (per-level `punt`) play the
    // optimal human strategy: hit the ball FLAT OUT so it rockets ahead, chase
    // at top speed, hit it again — instead of the polite matched-speed dribble.
    // Bends fall back to the dribble (a punted ball would just pinball).
    punt: {
      lookahead: 80, // the punted ball rolls far — need this much ahead reasonably straight
      maxBend: 0.18, // …no sharper than this (shallower bends just bank off the walls)
      deepLookahead: 135, // …and no hairpin building right behind the runway
      deepMaxBend: 0.45,
      maxBallErr: 4.5, // only punt a ball that's ON the line (else it flies off-course)
      window: 38, // start the flat-out run-up within this gap to the ball
    },

    aimWanderRate: 1.5, // how fast the aim error wanders (the amount is per-level)

    // Item decisions (M12): when a HELD item is worth firing. The per-level
    // `itemAggression` scales how fast the AI pulls the trigger once these fit.
    items: {
      boostMaxCorner: 0.14, // boost only when the bend ahead is gentler than this (rad)
      boostMinSpeed: 16, // …and already moving this fast
      boostMaxBallErr: 5, // …and the ball is within this of its line
      kickMaxCorner: 0.12, // kick = a long pass down a straight
      kickMaxBallErr: 4, // …with the ball cleanly on the line
      magnetMinBend: 0.03, // magnet is worth it when a bend is coming (carry the ball through)
      dozerMaxGap: 8, // dozer: a rival car within this far ahead…
      dozerMaxLateral: 3, // …roughly in my lane…
      dozerMinSpeed: 16, // …while moving fast enough to ram
      shieldMaxGap: 7, // shield: a rival breathing down my neck within this behind
      boxSeekAhead: 55, // how far ahead an empty-handed AI scans for item boxes
      // M104: 0.65 → 0.85. The blend is a steady-state aim offset, not a
      // latch — at 0.65 the aim sat 35% short of the box, so any box more
      // than ~7.4 u off the ball's lane (collectRadius 2.6 / 0.35) was chased
      // but systematically MISSED. 0.85 shrinks the miss band to ~3 u.
      boxSeekBlend: 0.85, // how strongly it bends its line toward a box (0..1)
      boxSeekMaxOff: 11, // only detour for a box within this lateral of the ball's lane
    },

    // Progress watchdog: if the ball makes NO forward progress while the car is
    // right up against it for this long, force a recovery (it's genuinely stuck).
    progressTimeout: 1.5, // seconds stuck-near-the-ball before forcing recovery
    recoverDist: 18, // count as "stuck" when the car is within this ACTUAL distance of the ball

    // CHASE-BACK (M77 — found in the owner's Legend telemetry): a ball
    // launched far BEHIND the car (his studs/walls/kicks, hairpin ricochets,
    // scorpion landings) left the driver only its slow regulated reverse
    // (~9 u/s net after PUSH/RECOVER flip-flop) — measured 30-110 s lost per
    // stall, THE structural mid-race fade in every trace. Past `enterGap` the
    // driver now U-TURNS and DRIVES forward to a staging point behind+beside
    // the ball (scrum-maneuver geometry), then resumes a normal PUSH.
    chase: {
      enterGap: -35, // ball this far behind the car → stop reversing, turn + drive
      // M84: HYSTERESIS — once a chase-back starts it LATCHES until the ball is
      // nearly ahead again (exitGap), instead of re-checking enterGap per tick.
      // Measured deadlock without it: a ball dying EXACTLY at the threshold
      // (dead ball 35 u behind, NYC legendary) pinned the car in a one-tick
      // PUSH-creep / K-turn-reverse limit cycle at the boundary — forever.
      // M104: exitGap −6 → +8. Released at −6 the car was still DOWN-course
      // of the ball FACING up-course; the handoff landed in RECOVER, whose
      // reverse (car backward = down-course here) drove it away until the
      // chase re-latched at −35 — a measured ~30 s ping-pong (Patagonia
      // medium, arc 797↔830). Positive exit = the U-turn journey COMPLETES:
      // the car passes beside the ball to the staging side and re-engages
      // PUSH with the ball genuinely ahead.
      exitGap: 8, // release only once the car is truly behind the ball again
      stageBehind: 9, // staging point this far up-course of the ball…
      stageSide: 6, // …offset to the side (never ram it deeper on the way in)
      nearDist: 25, // inside this range of the staging point, ease off…
      nearSpeed: 14, // …to this approach speed (a hot arrival just flies past)
      // M143c: FOLLOW THE ROAD BACK when the ball is far behind. The chase aimed
      // a straight line at its staging point; from a few hundred units away on
      // a curving course that line runs through a guardrail, and the car drove
      // nose-first into it and stayed (measured: Lagos, ball 470 behind, car
      // square to the rail at full throttle for the rest of the race). Beyond
      // `roadDist` it aims at the road itself, `roadLook` up-course.
      roadDist: 60,
      roadLook: 30,
      roadLateral: 8, // …keeping within this much of the centreline on the way
    },

    // WEDGE WATCHDOG (M143c): on the throttle and not moving. The progress
    // watchdog above only counts while the car is NEAR its ball; a car chasing
    // a ball that is far away could sit pinned against something for the rest
    // of the race (measured over 216 six-car races: about one car in 75 never
    // finished). After `after` seconds of that it backs straight out for
    // `backFor` seconds and tries again. Deliberately slower to act than the
    // progress watchdog (1.5 s), so every existing recovery gets its turn first.
    // The mirror case: RECOVER backs straight up, and with an obstacle or a
    // parked car right behind it the car pushes against that for its whole
    // two-second timeout, pops forward for a moment and backs into it again
    // (measured in Rio: 285 seconds of that, the ball 26 behind, too near for
    // the chase-back to take over). After `reverseAfter` seconds of backing up
    // without moving, the driver turns round and DRIVES to its ball instead.
    wedge: { speed: 0.8, after: 2.0, backFor: 0.8, reverse: 0.8, reverseAfter: 0.7 },

    // Hazard sense (M77, legendary `avoidHazards`): steer the aim point around
    // rival mud puddles + free-kick walls ahead in the lane. The owner drops
    // 9-11 items a race — an AI that eats every puddle can never keep his pace.
    hazard: {
      lookahead: 45, // scan this far down-course for a hazard in the lane
      margin: 3.5, // clear a hazard's edge by this much (units)
    },

    // Ball harassment (M77, legendary `harass`): mid-chase (own ball punted
    // well ahead), a Legend rival passing the leading human's ball swipes it
    // off-line — racing-game racecraft, strictly time-boxed so it never
    // sacrifices the AI's own race.
    harass: {
      range: 30, // prey ball must be within this far ahead of the car
      maxLateral: 9, // …and within this lateral offset of the car's path
      minOwnGap: 15, // only mid-chase: own ball at least this far ahead
      time: 1.2, // seconds per swipe attempt, then back to the race
      cooldown: 5, // seconds between attempts
    },

    // Stall escapes (M17 — found in the owner's race telemetry; rivals were
    // losing 16-85 SECONDS to two failure loops, which was the real source of
    // his blowout wins, not raw pace):
    // 1) A ball knocked back to the START has no room behind it — reversing to
    //    re-approach can never succeed there, so inside `scrumZone` the driver
    //    skips RECOVER entirely and just drives straight at the ball.
    scrumZone: 45,
    // 2) Repeated watchdog stalls at the same spot (`escalateAfter` resets
    //    within `sameSpot` — generous, so a CREEPING stall that drifts a few
    //    metres per cycle still counts as the same fight) hand control to the
    //    real-space maneuver — the car is usually parked BESIDE the ball
    //    there, where the normal along-track recovery fixes nothing.
    //    M143c: the same watchdog also counts a car that is LEVEL with a ball
    //    at rest on the far side of the road. Measured in full fields (Tokyo,
    //    New York): the car turns across toward the ball, passes the ball's
    //    arc before it gets there, reverses back the way it came, and repeats
    //    for the rest of the race, never coming inside `recoverDist`, so the
    //    stall was invisible. Level = the ball no more than `levelAhead` in
    //    front (and not further behind than `recoverDist`), at least
    //    `levelLateral` to one side, and slower than `restSpeed`.
    //    `maneuverDist` is how far from the ball the maneuver may run; the
    //    road is 36 wide, so the old 25 could not reach across it.
    stuck: {
      sameSpot: 45,
      escalateAfter: 2,
      levelAhead: 6,
      levelLateral: 8,
      restSpeed: 2,
      maneuverDist: 40,
    },
    // 3) Three-point turn: when the aim point is far BEHIND the nose and the
    //    car is slow, forward pure-pursuit just orbits (the target sits inside
    //    the minimum turning circle — seen as endless circling in telemetry).
    //    Backing up while counter-steering swings the nose around in place.
    kturn: { minErr: 2.2, maxSpeed: 8, reverse: 0.7 },

    // The AI's reverse engine (M31). The player's reverse was buffed to mirror
    // forward (tuning.car.reverse*) for snappy course-correction, but the AI's
    // recovery maneuvers (RECOVER, k-turn, swing, jam-bail) fire FIXED reverse
    // throttles and were tuned (M17) against a LOW reverse top that acts as a
    // natural speed governor — a fast reverse would send them rocketing backward
    // and break those loops. So the AI keeps the original values, pinned into its
    // car params by aiCarParamsFor(). Keep these = the pre-M31 tuning.car values.
    reverseAccel: 18,
    reverseTop: 15,
    brake: 58, // M39: AI keeps the pre-M39 brake (the HUMAN brake was raised to 100 for snappy stop→reverse)

    // ---- STATE MACHINE (PUSH / RECOVER) — see ai/Driver.ts ----
    // The car is always in exactly one mode. Switching needs a clear margin AND a
    // minimum dwell time in the mode ("stickiness"), which is what stops the
    // per-frame forward/back flip-flopping.
    fsm: {
      enterRecoverLead: 0.0, // ball no longer at least this far ahead (down-course) → leave PUSH
      exitRecoverLead: 14.0, // back up until the ball is this far ahead, then re-approach from distance
      minPushDwell: 0.2, // min seconds in a mode before it may switch out (anti-chatter)
      minRecoverDwell: 0.3,
      maxReverseTime: 2.0, // give up a stuck reverse after this → pop back to PUSH and re-approach forward
      reverseEps: 0.3, // backward speed past this = "actually reversing"
      reverseSpeed: -14, // regulate the reverse to this (slow) speed so it backs in under control
    },

    // ---- Difficulty levels: the SKILL knobs that scale ----
    // `cruiseSpeed` = how hard/fast it knocks the ball along (the main lever for
    // lap time — M12 raised these sharply: hard ≈ 86% of the player's 56,
    // legendary = full parity); `cornerSpeed` = the floor it slows to for the
    // sharpest bends; `aimWanderAmp` + `steerNoise` = its built-in error (what
    // makes it beatable); `itemAggression` = how decisively it fires items.
    defaultLevel: 'medium',
    // rubber = catch-up assist strength (cruise speed eases up/down with the gap
    // to the player's ball, max ±16% at 300m — keeps races close, OFF for
    // legendary so purists get a clean fight).
    // `punt: true` = plays the aggressive punt-and-chase game on straights
    // (the optimal human technique) instead of only the safe dribble.
    // `cornerTrust` (optional) overrides the shared cornerEase: LOWER = brakes
    // less for bends, trusting the dribble cradle to hold the ball — the other
    // half of how a skilled human carries pace. `line` (optional) overrides
    // lineCorrect: they actively steer the BALL onto the racing line.
    //
    // M15 (owner-approved "make them faster"): `carScale` gives the AI's CAR
    // more engine than the player's — accel/topSpeed/boost all scale by it.
    // This is the honest Mario-Kart-style cheat: the player's machine caps at
    // ~47 flat-out, so past hard the AI simply gets a faster car (hard ~+6%,
    // legendary ~+13% sustained). Rubber is now ASYMMETRIC per level:
    // `catchUp` = assist when BEHIND the player (so passing them never makes
    // them give up), `mercy` = how much they ease when AHEAD (legendary: none).
    //
    // M77 Legend rework (owner: "Legend can never catch me — should beat me
    // sometimes", target: a skilled player wins ~6-7 of 10):
    //   `ballPush`      — the AI's strike liveliness as a multiple of the stock
    //                     ball. 1.5 = the HUMAN's M39 buff (humanPushScale), so
    //                     Legend's punts finally travel as far as the owner's.
    //                     Measured per city: -13s London, -29s NYC solo. Kept
    //                     1.0 below legendary (the ladder's feel is untouched).
    //   `avoidHazards`  — steer around rival mud puddles + free-kick walls
    //                     (the owner carpet-bombs 9-11 items per race; dumb
    //                     AI ate all of it).
    //   `harass`        — clip the leading human's ball off-line in passing
    //                     when it costs nothing (racecraft — race the PLAYER,
    //                     not just the clock).
    //   legendary `catchUp` 0.55 → 1.15: breaking away no longer ends the
    //                     race — a dropped Legend pack hunts at up to +18%.
    // M83 BIG KID (owner: a mode where children can win): `escort: true` swaps
    // the linear catchUp/mercy rubber for the ESCORT curve (ai.escort below) —
    // the pack paces itself off the gap to the kid's ball ("play to the
    // player"): at/ahead of the kid they dawdle so any child outpaces them,
    // dropped far behind they ramp to full cruise + a chase bonus and visibly
    // hunt. catchUp/mercy are IGNORED when escort is on (kept for the table's
    // uniform shape). Driving-skill knobs are medium's — the rivals look
    // competent, only their PACE follows the kid.
    levels: {
      bigkid: {
        cruiseSpeed: 30,
        cornerSpeed: 14,
        aimWanderAmp: 2.0,
        steerNoise: 0.04,
        itemAggression: 0.35,
        catchUp: 1.0,
        mercy: 1.0,
        carScale: 1.0,
        punt: false,
        cornerTrust: 1.0,
        line: 0.0,
        ballPush: 1.0,
        avoidHazards: false,
        harass: false,
        catchUpEngine: 0,
        escort: true,
      },
      easy: {
        cruiseSpeed: 31,
        cornerSpeed: 14,
        aimWanderAmp: 3.5,
        steerNoise: 0.07,
        itemAggression: 0.5,
        catchUp: 1.0,
        mercy: 1.0,
        carScale: 1.0,
        punt: false,
        cornerTrust: 1.0,
        line: 0.0,
        ballPush: 1.0,
        avoidHazards: false,
        harass: false,
        catchUpEngine: 0,
        escort: false,
      },
      medium: {
        cruiseSpeed: 42,
        cornerSpeed: 17,
        aimWanderAmp: 2.0,
        steerNoise: 0.04,
        itemAggression: 0.75,
        catchUp: 1.0,
        mercy: 0.6,
        carScale: 1.0,
        punt: false,
        cornerTrust: 1.0,
        line: 0.0,
        ballPush: 1.0,
        avoidHazards: false,
        harass: false,
        catchUpEngine: 0,
        escort: false,
      },
      // NOTE: `line` must stay 0 — aiming off the ball's lane breaks the strike
      // geometry (the car arrives BESIDE the ball; measured: instant DNF).
      // NOTE (M16 dead ends, PRE-M84): carScale past ~1.10 used to collapse
      // ball control into recover-thrash. M104 RE-MEASURED with the M84
      // possession touch + the ladder-raised vehicle handling live: carScale
      // 1.12 (hard) / 1.18 (legendary) now hold cleanly (solo stalls ≈ 0
      // across all 9 cities) — the touch is what tames the hotter engine.
      // Catch-up beyond the machine still goes through ITEMS
      // (powerups.desperationGap).
      // NOTE (M77 dead end, measured): cornerTrust below ~0.6 pinballs the
      // livelier legendary ball off hairpin walls. M104: cornerSpeed 21/20
      // re-measured fine WITH the touch; the old "above ~20 pinballs" note
      // was also pre-touch.
      // M104 cruise targets look high vs the drag ceiling on purpose: the
      // P-controller (speedGain 0.1) droops ~15% below its target, so the
      // REALIZED pace is hard ≈ 50, legendary ≈ 55 — set targets by measuring
      // finish times (scratchpad eval), not by reading the number.
      hard: {
        cruiseSpeed: 58,
        cornerSpeed: 20,
        aimWanderAmp: 0.9,
        steerNoise: 0.02,
        itemAggression: 1.0,
        catchUp: 1.0,
        mercy: 0.2,
        carScale: 1.12,
        punt: true,
        cornerTrust: 0.62,
        line: 0.0,
        ballPush: 1.3,
        avoidHazards: true,
        harass: false,
        catchUpEngine: 0,
        escort: false,
      },
      legendary: {
        cruiseSpeed: 64,
        cornerSpeed: 21,
        aimWanderAmp: 0.3,
        steerNoise: 0.008,
        itemAggression: 1.3,
        catchUp: 1.5,
        mercy: 0,
        carScale: 1.18,
        punt: true,
        cornerTrust: 0.6,
        line: 0.0,
        ballPush: 1.5,
        avoidHazards: true,
        harass: true,
        catchUpEngine: 0.2,
        escort: false,
      },
    },
    rubberRange: 300, // gap (units) at which the rubber-band reaches full strength
    rubberMax: 0.16, // max cruise-speed adjustment (fraction)
    // M83 BIG KID escort curve (levels with `escort: true`) — replaces the
    // linear rubber above. Cruise scale is a straight ramp on the gap to the
    // kid's ball: gap ≤ 0 (at/ahead of the kid) → crawlScale; gap ≥ riseRange
    // (dropped behind) → chaseScale; linear between. The math that makes "the
    // kid usually wins" work: any AHEAD-of-the-kid equilibrium means the kid
    // finishes second, so the crawl must undercut ANY child's pace — the pack
    // then settles just BEHIND the kid's ball, wherever the kid's own pace
    // pins it on the ramp. The escort scale also softens cornerSpeed +
    // strikeMin in Driver.ts (both floor the speed cap well above a true
    // crawl otherwise — a floored "crawl" still outran a slow child).
    escort: {
      crawlScale: 0.28, // cruise fraction while at/ahead of the kid's ball (~8 u/s)
      chaseScale: 1.2, // cruise fraction when dropped riseRange behind (~36 u/s — reels a runaway kid in)
      riseRange: 90, // gap (units behind the kid) over which crawl ramps to chase
    },
    // M77 engine catch-up shape (see aiCarParamsRubber): the ENGINE rubber
    // ramps over a SHORTER range than the cruise rubber (a pack 150u back is
    // already out of the fight at Legend pace), and only engages while the car
    // is AWAY from its ball — ferrying back / chasing a punt. Near the ball
    // the stock engine + approach control keep the strike clean (the measured
    // M16 thrash came from extra accel fighting the approach cap).
    catchUpEngineRange: 150, // gap (units) at which catchUpEngine reaches full strength
    catchUpEngageDist: 35, // car this close to its ball → stock engine (clean strikes)
  },

  /**
   * Roadside scenery detail (M36 — crowds + richer greenery). All visual-only,
   * all INSTANCED (a handful of draw calls), and none cast shadows, so this is
   * cheap on a mid-range phone. Tune freely.
   */
  scenery: {
    // People crowds on beaches, parks, plazas, markets, promenades, stadium
    // approaches. This scales how OFTEN a crowd cluster appears (not its size):
    // 0 = no people anywhere, 0.5 = half as dense, 1 = full. Drop it if a low
    // phone struggles (crowds are instanced + shadowless, so it rarely matters).
    crowdDensity: 1,
    // Foliage canopy richness: overlapping leaf blobs per tree. 1 = the old
    // single perfect ball; 3 = a fuller, irregular, less "cloned" canopy. More
    // blobs = a few more triangles, but still ONE draw call for all a city's
    // leaves. Trunks/positions are unchanged — only the canopy shape.
    treeBlobs: 3,
  },

  /** Game-feel effects (M10) — all visual-only, all skippable by reduced motion. */
  fx: {
    shakeDozer: 0.55, // camera shake when YOU get bulldozed (world units)
    shakeLand: 0.3, // …when you land a jump
    shakeBallHitMax: 0.22, // …cap for hard ball strikes
    shakeDecay: 4.2, // how fast shake settles (per second)
    shakeBoost: 0.035, // M145: the steady rumble while YOUR boost burns
    dustCount: 12, // particles per landing puff
  },

  /** M85/M89 — the REAL-FILE soundtrack: the menu anthem playlist (M85) and
   *  per-city RACE SONGS (M89). Files live in src/assets/music/menu/ and
   *  src/assets/music/race/ ("Dribble Racer <City>.mp3" — drop a song named
   *  after its city and the next build wires it to that city's races, zero
   *  code). All gains ride the MUSIC volume setting + the fanfare duck (they
   *  sit on the music bus). Masters reference ≈ -12 LUFS (per-file loudness
   *  trims in Soundtrack.ts normalize outliers), so menuGain 0.9 lands menu
   *  music around the -17 LUFS background sweet spot at default settings and
   *  raceGain 0.75 sits the race bed ~2.5 dB under that — engine + sfx ride
   *  on top. Cities WITHOUT a song race to the anthem at raceGain. */
  soundtrack: {
    menuGain: 0.9, // anthem level on menu screens (1 = the file's full level)
    transitionGain: 0.9, // tour loading/transition clip level (dormant since M89)
    fadeInS: 0.7, // fade up when a menu track starts/resumes
    landingFadeS: 2.2, // the session's FIRST start (landing on the title) — extra gentle

    celebrationGain: 0.9, // the Champion song over the podium cinematic + final standings (M96)
    raceGain: 0.75, // city-song bed level while racing (leaves room for engine + sfx)
    raceFadeInS: 1.8, // city song fade-up on the transition/intro screen (M89)
    raceFadeS: 1.0, // file deck fade OUT if the synth groove must take over (fallback)
    trackGapS: 2.0, // breath of silence between playlist tracks (and loops)
  },

  /** Procedural track generation. */
  track: {
    step: 4, // distance between centreline nodes
    halfWidth: 18, // road half-width (wider than the prototype so the ball stays dribblable)
    nodeCount: 560, // ~length of the course
    turnCurvatureMin: 0.04, // gentlest turn
    turnCurvatureMax: 0.075, // sharpest default turn (per-city difficulty can raise this in M8)
    headingClampRad: 1.2, // keep heading within ~±69° of forward (no spirals/overlap)
    sectionLenMin: 10, // min nodes per straight/curve section
    sectionLenMax: 34, // max nodes per section
    // Course END walls (M42): a cross-road barrier this far BEHIND the start
    // grid (so a knocked-back ball can never roll off the back of the course)
    // and one this far PAST the last road node (closing the finish run-out).
    // The road/ground/guardrails are meshed out to these barriers too.
    startBarrier: 6,
    endBarrier: 12,
    // THE START APRON (M143c). The start box is `startBarrier` deep and a car
    // is nearly 4 long, so a ball that comes to rest in the box (or within a
    // unit or two past the line) cannot be struck from behind: there is no
    // room between it and the barrier for a car. Measured in full fields, a
    // computer car facing that ball cycled its recovery moves for the rest of
    // the race, and a player has the same problem. So the floor of the box
    // tilts toward the course: a grounded ball short of `reach` past the start
    // line is carried down-course at `current` (gaining at most `accel` a
    // second) and rolls out to where it can be played. Grid balls start at
    // racers.ballAhead (7) and are never inside it.
    startApron: { reach: 3, current: 6, accel: 30 },
  },

  /** THE FINISH GOAL (M157, owner: "the finish line will be the balls actually
   *  going into a soccer goal… add a net that catches the balls and cars").
   *  The goal stands ON the finish line, the full road width; the net behind
   *  it is a soft spring that slows anything entering it to a stop with a
   *  small bounce back, so finished balls and cars settle INSIDE the goal.
   *  The finish rule itself is unchanged (a ball's centre crossing the line).
   *  Stateless — a pure function of position + velocity — so online
   *  prediction re-derives it exactly. Touching any of these changes the sim:
   *  bump SIM_VERSION. Proportions: a real 7.32 × 2.44 m goal scaled to the
   *  36-wide road is ×4.9, so the crossbar sits ~12 up and the net ~10 deep. */
  goal: {
    height: 12, // crossbar UNDERSIDE above the goal line's road
    depth: 10, // back net at turf level, measured from the goal line
    depthTop: 5, // back net at crossbar height (the roof net's length)
    postRadius: 0.42, // posts + crossbar (the line is painted the same width)
    // The net: a damped spring on whatever pushes past its rest surface.
    // ζ ≈ 0.7 (damping ≈ 2·0.7·√stiffness): a 45 u/s ball bulges it ~4.5,
    // then drops near-dead — a ~5% bounce back, so it settles well inside.
    stiffness: 21, // pull back per unit of stretch (u/s² per u)
    damping: 6.4, // per second, on the speed into/out of the net
    maxStretch: 7, // the net never gives further than this past its rest
    grip: 3, // per second: the mesh grabs motion ALONG it (caught, not slid)
    crossbarRestitution: 0.55, // a ball clipping the bar rings off it
  },

  /** Chase camera. */
  camera: {
    back: 10, // base distance behind the car
    backPerSpeed: 0.035, // distance grows a little with speed
    up: 4.4, // height above the car
    lookAhead: 5, // look slightly ahead of the car
    yawFollow: 9, // how fast the camera yaw eases behind the car
    fovBase: 68, // base field of view (degrees)
    fovBoostPunch: 12, // extra FOV held while boosting (sense of speed)
    // M145 (owner: "more of a zooming boost effect") — the moment a boost
    // lights the view kicks WIDER than the held punch and settles back, the
    // camera falls behind the car (it surges away, then is reeled in) and
    // sinks a touch toward the road.
    fovBoostKick: 9, // extra FOV on top of the punch at ignition (degrees)
    fovBoostKickDecay: 3.4, // how fast that kick settles (per second)
    boostPullBack: 1.5, // how far the camera falls behind a boosting car
    boostPullRate: 4, // how fast it falls back / catches up (per second)
    boostDrop: 0.14, // camera height lost per unit of pull-back
    fogNear: 60,
    fogFar: 360,
  },

  /** Racers + the start grid (M87: every offline field is 6 CPU rivals). */
  racers: {
    // Start lane slots in join order (player always slot 0, the centre).
    // One row across the road — slot 8 hugs the right wall in a full field.
    laneSlots: [0, -4, 4, -8, 8, -12, 12, 15],
    ballAhead: 7, // each racer's ball starts this far ahead of their car
    // Per-rival pace multipliers by field size (rival join order, player
    // excluded). Big fields ship 2 ACES (slightly quicker than the pack) and
    // 2 BACKMARKERS (a bit slower) — solid competition without a wall of
    // identical robots. 1.0 = the city difficulty exactly. The 6 row is the
    // M87 fixed offline field; 3/5/7 stay for tests + the online 2–7 fields.
    // M104: backmarkers 0.9 → 0.93 — at 0.9 the two slowest rivals sat ~7%
    // under even the STOCK human's flat-out pace and read as traffic, not
    // competition; 0.93 keeps the spread without the dead weight.
    fieldSkill: {
      3: [1, 1, 1],
      5: [1.07, 1.07, 1, 0.93, 0.93],
      6: [1.07, 1.07, 1, 1, 0.93, 0.93],
      7: [1.07, 1.07, 1, 1, 1, 0.93, 0.93],
    } as Record<number, number[]>,
  },
};

export type Tuning = typeof tuning;

/** The AI rival difficulty levels (keys of `tuning.ai.levels`). */
export type DifficultyLevel = keyof typeof tuning.ai.levels;

/** The ladder, easiest → hardest — chip rows + save validation share this ONE
 *  list so a new level only ever gets added in `tuning.ai.levels`. */
export const DIFFICULTY_LEVELS = Object.keys(tuning.ai.levels) as DifficultyLevel[];

/** Coerce anything (an old/corrupt save blob) to a valid level. */
export function asDifficulty(v: unknown, fallback: DifficultyLevel = 'medium'): DifficultyLevel {
  return DIFFICULTY_LEVELS.includes(v as DifficultyLevel) ? (v as DifficultyLevel) : fallback;
}

/**
 * The CAR PHYSICS an AI rival drives at a given level (M15): the player's car
 * with every speed parameter scaled by the level's `carScale` (accel, top
 * speed, boost — not brakes or steering, so they corner honestly). The AI's
 * REVERSE is always pinned back to the slow, controlled `tuning.ai.reverse*`
 * values (M31) so the buffed human reverse never destabilizes the tuned
 * recovery maneuvers. Memoized by scale (easy/medium share scale 1); used by
 * Race AND the AI pace tests so the two can never drift apart.
 */
const aiParamsCache = new Map<number, typeof tuning.car>();
export function aiCarParamsFor(level: DifficultyLevel): typeof tuning.car {
  const scale = tuning.ai.levels[level].carScale;
  let p = aiParamsCache.get(scale);
  if (!p) {
    p = {
      ...tuning.car,
      wallAssist: false, // M40/M49: the AI keeps its tuned M14/M17 wall behaviour
      accel: tuning.car.accel * scale,
      topSpeed: tuning.car.topSpeed * scale,
      boostTop: tuning.car.boostTop * scale,
      boostAccel: tuning.car.boostAccel * scale,
      reverseAccel: tuning.ai.reverseAccel,
      reverseTop: tuning.ai.reverseTop,
      brake: tuning.ai.brake, // M39: keep the AI on the pre-M39 brake; only the human got the hard brake
      reverseEngage: 0, // M92: AI keeps the exact-zero brake→reverse gate its maneuvers are tuned around
    };
    aiParamsCache.set(scale, p);
  }
  return p;
}

/**
 * M77 — ENGINE catch-up for Legend. The M15 cruise rubber is impotent at
 * legendary: its cruise target (55) already exceeds the car's drag ceiling
 * (~52 at carScale 1.1), so raising the TARGET when behind changed nothing —
 * measured in the owner's ghost-trace eval (gaps at his finish identical with
 * catchUp 0.55 vs 1.15). Real catch-up needs a faster MACHINE: the level's
 * `catchUpEngine` scales the whole engine by up to that fraction as the gap
 * to the leading human approaches `rubberRange`. Gap is QUANTIZED to eighths
 * so the params stay memoized (and don't jitter per step). 0 = the plain
 * level params, byte-identical to aiCarParamsFor.
 */
export function aiCarParamsRubber(level: DifficultyLevel, gapToLeader: number): typeof tuning.car {
  const lvl = tuning.ai.levels[level];
  if (lvl.catchUpEngine <= 0 || gapToLeader <= 0) return aiCarParamsFor(level);
  const q = Math.round(Math.min(gapToLeader / tuning.ai.catchUpEngineRange, 1) * 8) / 8;
  if (q <= 0) return aiCarParamsFor(level);
  const scale = lvl.carScale * (1 + lvl.catchUpEngine * q);
  let p = aiParamsCache.get(scale);
  if (!p) {
    p = {
      ...aiCarParamsFor(level),
      accel: tuning.car.accel * scale,
      topSpeed: tuning.car.topSpeed * scale,
      boostTop: tuning.car.boostTop * scale,
      boostAccel: tuning.car.boostAccel * scale,
    };
    aiParamsCache.set(scale, p);
  }
  return p;
}

/**
 * The CAR PHYSICS the HUMAN drives in a given vehicle (M49): the stock
 * `tuning.car` with the vehicle's stat profile applied — engine (top speed /
 * accel, boost included) and handling (turn radius + yaw cap). Weight is NOT
 * in here (it lives in the collision layer — see SimCore). Memoized per
 * vehicle id; an unknown id is exactly the stock params (fail-safe). M104:
 * the old `'sports'` short-circuit is GONE — sports is no longer the 1.0
 * baseline (the upgrade ladder lifted it), so it derives like everyone else.
 */
const vehicleParamsCache = new Map<string, typeof tuning.car>();
export function vehicleCarParamsFor(vehicleId: string): typeof tuning.car {
  const v = tuning.vehicles[vehicleId];
  if (!v) return tuning.car;
  let p = vehicleParamsCache.get(vehicleId);
  if (!p) {
    p = {
      ...tuning.car,
      topSpeed: tuning.car.topSpeed * v.topSpeed,
      boostTop: tuning.car.boostTop * v.topSpeed,
      accel: tuning.car.accel * v.accel,
      boostAccel: tuning.car.boostAccel * v.accel,
      baseTurnRadius: tuning.car.baseTurnRadius / v.handling,
      turnRadiusGainPerSpeed: tuning.car.turnRadiusGainPerSpeed / v.handling,
      maxYaw: tuning.car.maxYaw * v.handling,
    };
    vehicleParamsCache.set(vehicleId, p);
  }
  return p;
}

/** A vehicle's collision weight (1 = the stock car). See tuning.vehicles. */
export function vehicleWeightFor(vehicleId: string): number {
  return tuning.vehicles[vehicleId]?.weight ?? 1;
}

/** The ball-contact box of a vehicle (M153, see tuning.vehicles `box`). An
 *  unknown id gets the stock box — the old shared 3.8 × 2.3 footprint —
 *  which is also what every pure collision test drives against. The object
 *  is the tuning row itself (stable identity, no per-step allocation). */
export const STOCK_CAR_BOX = { halfLength: 1.9, halfWidth: 1.15 } as const;
export function vehicleBoxFor(vehicleId: string): { halfLength: number; halfWidth: number } {
  return tuning.vehicles[vehicleId]?.box ?? STOCK_CAR_BOX;
}

/**
 * The ball-push params a HUMAN in a given vehicle strikes with (M49/M51): the
 * M39 human hard-strike buff (humanPushScale) plus a touch of the vehicle's
 * weight as heft. 'sports' is byte-identical to the old single humanBallPush.
 * Memoized — the sim reads this in its hot collision loop. The AI always
 * pushes with the plain tuned ball (the pace ladder's contract).
 */
const ballPushCache = new Map<string, typeof tuning.ball>();
export function vehicleBallPushFor(vehicleId: string): typeof tuning.ball {
  let p = ballPushCache.get(vehicleId);
  if (!p) {
    const weight = vehicleWeightFor(vehicleId);
    p = {
      ...tuning.ball,
      carPush: tuning.ball.carPush * tuning.ball.humanPushScale * (0.85 + 0.15 * weight),
    };
    ballPushCache.set(vehicleId, p);
  }
  return p;
}

/**
 * The POSSESSION TOUCH a given vehicle dribbles with (M84): tuning.possession
 * scaled by the vehicle's HANDLING stat — the better the handling, the stronger
 * the pull AND the bigger a knock it takes to strip possession (breakSpeed).
 * scale = 1 + (handling − 1) × handlingInfluence, floored at minScale. M104
 * ladder: coupe ≈ 1.25× (the M84b starter feel, unchanged) → pickup 1.3 →
 * muscle 1.35 → van 1.4 → sports 1.45 → motorcycle ≈ 1.63× → f1 1.7× (the
 * M114 crown) — no unlock ever
 * dribbles worse than the starter. Applies to HUMANS and BOTS alike (a bot's
 * vehicle identity is sim truth since M51, like its weight).
 * Memoized — the sim reads this every step for every racer.
 */
const possessionCache = new Map<string, typeof tuning.possession>();
export function possessionParamsFor(vehicleId: string): typeof tuning.possession {
  let p = possessionCache.get(vehicleId);
  if (!p) {
    const base = tuning.possession;
    const handling = tuning.vehicles[vehicleId]?.handling ?? 1;
    const scale = Math.max(base.minScale, 1 + (handling - 1) * base.handlingInfluence);
    p =
      scale === 1
        ? base
        : {
            ...base,
            gain: base.gain * scale,
            pull: base.pull * scale,
            breakSpeed: base.breakSpeed * scale,
          };
    possessionCache.set(vehicleId, p);
  }
  return p;
}

/**
 * The ball-push params an AI strikes with at a given level (M77): the level's
 * `ballPush` multiplier on the stock ball. Scale 1 returns tuning.ball ITSELF
 * (identity), so easy/medium/hard collisions stay byte-identical to the old
 * "AI always pushes with the plain tuned ball" contract — only Legend (1.5,
 * matching the human's M39 buff) actually diverges. Memoized per level.
 */
const botBallPushCache = new Map<number, typeof tuning.ball>();
export function botBallPushFor(level: DifficultyLevel): typeof tuning.ball {
  const scale = tuning.ai.levels[level].ballPush;
  if (scale === 1) return tuning.ball;
  let p = botBallPushCache.get(scale);
  if (!p) {
    p = { ...tuning.ball, carPush: tuning.ball.carPush * scale };
    botBallPushCache.set(scale, p);
  }
  return p;
}
